<template>
  <div
    class="e-w-full e-overflow-hidden e-border e-border-gray-300 e-rounded-md e-shadow-sm e-px-4 e-py-3"
  >
    <div
      class="grid-table__container e-relative e-w-full e-h-full e-flex e-flex-col e-overflow-y-auto e-overflow-x-hidden"
    >
      <div
        v-if="cameras.length"
        class="grid-table grid-table__headers e-gap-2 e-bg-gray-100 e-p-2 e-font-bold e-text-gray-700 e-sticky e-top-0 e-border-solid e-border-b e-border-gray-200 e-mb-2"
      >
        <div
          v-for="header in headers"
          :key="header"
          class="e-flex e-justify-center"
        >
          {{ header }}
        </div>
      </div>
      <div class="e-divide-y e-divide-gray-200 grid-table__body">
        <div
          v-for="camera in cameras"
          :key="camera.exid"
          class="grid-table grid-table__body-item e-hover:e-bg-gray-50 e-items-center e-py-2 e-px-3 e-mb-0.5 e-bg-gray-200"
        >
          <ETruncatedDiv
            class="e-flex e-justify-center cursor-pointer primary--text"
            width="9rem"
            fixed-width
            @click="$emit('open-camera-dialog', camera)"
            >{{ camera.name }}
          </ETruncatedDiv>
          <div class="e-flex e-justify-center">
            <div>{{ camera.exid }}</div>
          </div>
          <div class="e-flex e-justify-center">
            <div
              :class="{
                'cursor-pointer primary--text': camera.contextCameraExid,
              }"
              @click="onClickOnContextCamera(camera)"
            >
              {{ camera.contextCameraExid || "N/A" }}
            </div>
          </div>
          <div class="e-flex e-justify-center caption">
            <span :class="getStatusColor(camera.status)">
              {{ getStatusText(camera.status) }}
            </span>
          </div>
          <div class="e-flex e-justify-center">
            <div class="e-flex e-flex-wrap e-gap-1">
              <EReadMore :content="getFeatureFlags(camera)" :max-length="2" />
            </div>
          </div>
          <div class="e-flex e-justify-center">
            {{ camera.coolifyServiceName }}
          </div>
          <div class="e-flex e-justify-center">
            {{ camera.branchName }}
          </div>
          <div class="e-flex e-justify-center">
            {{ camera.modelVersion }}
          </div>
          <div class="e-flex e-justify-center">
            {{ camera.coolifyServerName }}
          </div>
          <div
            class="e-flex e-justify-center"
            :class="appsStatusColorText(camera.coolifyServiceStatus)"
          >
            {{ camera.coolifyServiceStatus }}
          </div>
          <div class="e-flex e-justify-center">
            {{ formatDate(camera.startTime) }}
          </div>
          <div class="e-flex e-justify-center">
            <ButtonTooltip
              v-if="!isAppRunning(camera.coolifyServiceStatus)"
              icon
              x-small
              :loading="isStarting[camera.coolifyServiceUuid]"
              tooltip-text="Start"
              icon-name="fas fa-play"
              :icon-props="{ color: 'green' }"
              button-class="cursor-pointer ml-1"
              @click="startService(camera)"
            />
            <ButtonTooltip
              v-else
              icon
              x-small
              :loading="isPausing[camera.coolifyServiceUuid]"
              tooltip-text="Pause"
              icon-name="fas fa-pause"
              :icon-props="{ color: 'red' }"
              button-class="cursor-pointer ml-1"
              @click="pauseService(camera)"
            />
            <ButtonTooltip
              icon
              x-small
              :loading="isRestarting[camera.coolifyServiceUuid]"
              tooltip-text="Restart"
              icon-name="fas fa-rotate"
              :icon-props="{ color: 'primary' }"
              button-class="cursor-pointer ml-1"
              @click="restartService(camera)"
            />
            <ButtonTooltip
              icon
              x-small
              tooltip-text="Edit"
              icon-name="fas fa-pencil"
              :icon-props="{ color: 'primary' }"
              button-class="cursor-pointer ml-1"
              @click="$emit('edit-app-dialog', camera)"
            />
            <ButtonTooltip
              icon
              x-small
              :loading="isDeleting[camera.coolifyServiceUuid]"
              tooltip-text="Delete"
              icon-name="fas fa-trash"
              :icon-props="{ color: 'error' }"
              button-class="cursor-pointer ml-1"
              @click="openDeleteDialog(camera)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue"
import CameraUtils from "@/mixins/cameraUtils"
import ProjectUtils from "~/mixins/projectUtils"
import { AiApi } from "@evercam/shared/api"
import ButtonTooltip from "@evercam/shared/components/ButtonTooltip.vue"

export default Vue.extend({
  name: "AnprGateReportCamerasTable",
  components: { ButtonTooltip },
  mixins: [CameraUtils, ProjectUtils],
  props: {
    cameras: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    project: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      headers: [
        "Name",
        "ID",
        "Context Camera ID",
        "Status",
        "Feature Flags",
        "Service Name",
        "Branch Name",
        "Model Version",
        "Server",
        "Service Status",
        "Created At",
        "Actions",
      ],
      isStarting: {},
      isPausing: {},
      isRestarting: {},
      isDeleting: {},
    }
  },
  methods: {
    isAppRunning(status: string) {
      return status.includes("running:")
    },
    getFeatureFlags(item) {
      return (
        item?.featureFlags?.map((feature) => this.toTitleCase(feature)) || []
      )
    },
    onClickOnContextCamera(camera) {
      if (!camera.contextCameraExid) {
        return
      }
      this.$emit("open-camera-dialog", { exid: camera.contextCameraExid })
    },
    async startService(item) {
      this.isStarting = {
        [item.coolifyServiceUuid]: true,
      }

      try {
        await AiApi.coolify.startService(item.coolifyServiceUuid)
        this.$notifications.success("Service started successfully.")
      } catch (error) {
        console.error("Error starting service", error)
        this.$notifications.error({
          text: "Service start failed!",
          error,
        })
      } finally {
        this.isStarting = {}
      }
    },
    async pauseService(item) {
      this.isPausing = {
        [item.coolifyServiceUuid]: true,
      }
      try {
        await AiApi.coolify.pauseService(item.coolifyServiceUuid)
        this.$notifications.success("Service paused successfully.")
      } catch (error) {
        console.error("Error pausing service", error)
        this.$notifications.error({
          text: "Service pause failed!",
          error,
        })
      } finally {
        this.isPausing = {}
      }
    },

    async restartService(item) {
      this.isRestarting = {
        [item.coolifyServiceUuid]: true,
      }

      try {
        await AiApi.coolify.restartService(item.coolifyServiceUuid)
        this.$notifications.success("Service restarted successfully.")
      } catch (error) {
        console.error("Error restarting service", error)
        this.$notifications.error({
          text: "Service restart failed!",
          error,
        })
      } finally {
        this.isRestarting = {}
      }
    },
    appsStatusColorText(status: string) {
      return this.isAppRunning(status)
        ? "green--text text--darken-2"
        : "red--text text--darken-2"
    },
    async deleteApp(app) {
      this.isDeleting = {
        [app.coolifyServiceUuid]: true,
      }

      try {
        await AiApi.coolify.deleteApp({
          projectExid: this.project.exid,
          cameraExid: app.exid,
          appId: app.id,
          appUuid: app.coolifyServiceUuid,
        })

        this.$notifications.success("App deleted successfully.")
      } catch (error) {
        this.$notifications.error({ text: "Delete App failed", error })
      } finally {
        this.$root.$emit("refresh-report-table")
      }
    },
    async openDeleteDialog(item) {
      if (
        await this.$confirmDialog.open({
          title: "Delete App",
          message: `Are you sure you want to delete the App <span class='font-weight-bold'>${item.coolifyServiceName}</span>? <br> This action cannot be undone!`,
        })
      ) {
        this.deleteApp(item)
      }
    },
  },
})
</script>

<style lang="scss">
.grid-table {
  display: grid;
  z-index: 3;
  padding: 0;
  background: rgba(241, 243, 246, 0.35);
  border-radius: 0.25rem;
  grid-template-columns: repeat(11, minmax(0, 1fr)) minmax(0, 1.5fr);

  * {
    font-size: 12px;
  }

  &__container {
    &::before {
      content: "";
      position: absolute;
      display: block;
      top: -1px;
      left: 0;
      width: 100%;
      background: white;
      height: 0.5rem;
    }
  }

  &__headers {
    background: #c8d2d7;
    border: 1px solid #d3d3d3;
    & > div {
      padding: 0.35rem 0;
    }
  }

  &__body {
    & > div {
      &:hover {
        background: #e3e3e3;
      }
    }
  }

  &__body-item {
    border: 1px solid #d3d3d3;
    padding: 0.25rem;
    font-size: 0.5rem;
  }
}
</style>
